import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { reviewService } from '../services/api';

interface ReviewUser {
  id: number;
  displayName: string;
  profilePicture?: string;
}

interface ReviewListing {
  id: number;
  bundleName?: string | null;
  customCardName?: string | null;
  isBundle?: boolean;
  card?: {
    id: number;
    name: string;
    cardNumber?: string;
    imageUrl?: string;
  };
}

interface ReviewTransaction {
  id: number;
  agreedPrice?: number;
  status?: string;
  listing?: ReviewListing;
}

interface TransactionReview {
  id: number;
  rating: number;
  comment?: string | null;
  createdAt: string;
  reviewer?: ReviewUser;
  transaction?: ReviewTransaction;
}

interface ReviewsResponse {
  user: ReviewUser;
  summary: {
    averageRating: number;
    totalReviews: number;
    ratingBreakdown: Record<number, number>;
  };
  reviews: TransactionReview[];
}



const UserReviewsPage = () => {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const [data, setData] = useState<ReviewsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (userId) {
      fetchReviews(1);
    }
  }, [userId]);

  const fetchReviews = async (pageToLoad: number) => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await reviewService.getUserReviews(userId, { page: pageToLoad, limit: 10 });
      setData(response.data.data);
      setPage(response.data.pagination?.page || pageToLoad);
      setTotalPages(response.data.pagination?.totalPages || 1);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch reviews', err);
      setError(err.response?.data?.message || t('userReviews.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const scoreLabel = useMemo(() => {
    const avg = data?.summary.averageRating || 0;
    if (avg >= 4.5) return t('userReviews.labels.outstanding');
    if (avg >= 4) return t('userReviews.labels.great');
    if (avg >= 3) return t('userReviews.labels.solid');
    if (avg > 0) return t('userReviews.labels.needsAttention');
    return t('userReviews.labels.noReviews');
  }, [data, t]);

  const renderStars = (rating: number) => (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={star <= rating ? 'text-amber-500' : 'text-gray-300'}>
          ★
        </span>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-10">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('userReviews.errors.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container mx-auto px-4 py-10">
        <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error || t('userReviews.errors.userLoadFailed')}
        </div>
      </div>
    );
  }

  const { user, summary, reviews } = data;
  const breakdown = summary.ratingBreakdown || {};

  return (
    <div className="container mx-auto px-4 py-10 max-w-5xl">
      <div className="bg-white shadow-lg rounded-2xl p-6 md:p-8 border border-gray-100">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-4">
            {user.profilePicture ? (
              <img src={user.profilePicture} alt={user.displayName} className="w-16 h-16 rounded-full object-cover border" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-blue-600 text-white flex items-center justify-center text-2xl font-bold">
                {user.displayName.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold">{user.displayName}</h1>
              <p className="text-gray-600">{t('userReviews.reliabilityScore')}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-gray-500 text-sm uppercase tracking-wide">{t('userReviews.reliability')}</p>
            <div className="flex items-center justify-end gap-2 text-3xl font-bold">
              <span>{summary.averageRating.toFixed(1)}</span>
              <span className="text-amber-500">★</span>
            </div>
            <p className="text-sm text-gray-600">{scoreLabel}</p>
            <p className="text-sm text-gray-500 mt-1">{summary.totalReviews} {summary.totalReviews === 1 ? t('userReviews.review') : t('userReviews.reviews')}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-8">
          <div className="md:col-span-1 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
            <p className="text-sm font-semibold text-blue-900 mb-3">{t('userReviews.ratingBreakdown')}</p>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = breakdown[star] || 0;
                return (
                  <div key={star} className="flex items-center gap-2">
                    <span className="w-12 text-sm text-gray-700">{star} ★</span>
                    <div className="flex-1 h-2 bg-white rounded-full overflow-hidden shadow-inner">
                      <div
                        className="h-full bg-blue-500"
                        style={{
                          width: summary.totalReviews ? `${(count / summary.totalReviews) * 100}%` : '0%'
                        }}
                      />
                    </div>
                    <span className="text-sm text-gray-700 w-10 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{t('userReviews.recentReviews')}</h2>
              <div className="text-sm text-gray-600">
                {t('userReviews.page', { page, totalPages })}
              </div>
            </div>

            {reviews.length === 0 ? (
              <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl p-6 text-center text-gray-600">
                {t('userReviews.noReviewsYet')}
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => {
                  const listingTitle =
                    review.transaction?.listing?.isBundle
                      ? review.transaction?.listing?.bundleName || t('userReviews.listing.bundle')
                      : review.transaction?.listing?.card?.name ||
                      review.transaction?.listing?.customCardName ||
                      t('userReviews.listing.marketplace');

                  return (
                    <div key={review.id} className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <div className="flex items-center gap-2">
                            {review.reviewer ? (
                              <Link to={`/users/${review.reviewer.id}/reviews`} className="font-semibold text-blue-700 hover:underline">
                                {review.reviewer.displayName}
                              </Link>
                            ) : (
                              <span className="font-semibold text-gray-800">{t('userReviews.listing.unknown')}</span>
                            )}
                            <span className="text-gray-400 text-xs">•</span>
                            <span className="text-gray-500 text-sm">
                              {new Date(review.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="mt-1 text-sm text-gray-600">
                            {listingTitle} · ฿{review.transaction?.agreedPrice?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) ?? '—'}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {renderStars(review.rating)}
                          <span className="font-semibold text-gray-800">{review.rating}/5</span>
                        </div>
                      </div>
                      {review.comment && (
                        <p className="mt-3 text-gray-800">{review.comment}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <button
                  onClick={() => fetchReviews(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50 bg-white hover:bg-gray-50"
                >
                  {t('userReviews.previous')}
                </button>
                <button
                  onClick={() => fetchReviews(page + 1)}
                  disabled={page >= totalPages}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50 bg-white hover:bg-gray-50"
                >
                  {t('userReviews.next')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserReviewsPage;
